import { api } from "../api";
import { el } from "../lib/dom";
import { errorBox } from "../lib/ui";
import type { AdminUser, AppSummary, Feedback, FeedbackStats, Grant, UserRole } from "../types";

const roleOf = (u: AdminUser): UserRole => u.role ?? (u.is_admin ? "super_admin" : "member");
const isBlanket = (u: AdminUser): boolean => roleOf(u) !== "member";

/** Consolidated feedback view: stats · request campaigns (reusing the Team
 *  filters to pick recipients) · responses with an act/shelve lifecycle. */
export async function renderFeedbackTab(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Feedback"]));

  let stats: FeedbackStats;
  let users: AdminUser[] = [];
  let apps: AppSummary[] = [];
  let grants: Grant[] = [];
  let initial: Feedback[] = [];
  try {
    [stats, users, apps, grants, initial] = await Promise.all([
      api.adminFeedbackStats(),
      api.adminUsers(),
      api.listApps(),
      api.adminGrants(),
      api.adminFeedbackList("new").then((r) => r.rows),
    ]);
  } catch {
    body.append(errorBox("Couldn't load feedback."));
    return;
  }

  // ── stats ──
  const statCard = (label: string, value: string | number) =>
    el("div", { class: "stat-card" }, [
      el("div", { class: "stat-val" }, [String(value)]),
      el("div", { class: "stat-lbl" }, [label]),
    ]);
  body.append(
    el("div", { class: "feedback-stats" }, [
      statCard("Requests sent", stats.requests.sent),
      statCard("Responded", `${stats.requests.responded} · ${stats.requests.response_rate}%`),
      statCard("New", stats.feedback.new),
      statCard("Actioned", stats.feedback.actioned),
      statCard("Shelved", stats.feedback.shelved),
    ]),
  );

  // ── agent: suggest recipients from usage (audit-driven, personalized) ──
  const suggestBtn = el("button", { class: "btn btn-primary btn-sm", type: "button" }, [
    "Analyze usage & suggest",
  ]);
  const suggestFb = el("span", { class: "muted-sm" });
  const proposalsHost = el("div", { class: "proposals" });
  const sendSelBtn = el("button", { class: "btn btn-primary", type: "button" }, [
    "Send selected",
  ]) as HTMLButtonElement;
  sendSelBtn.style.display = "none";
  const picks: {
    email: string;
    app_key: string;
    promptEl: HTMLTextAreaElement;
    cb: HTMLInputElement;
  }[] = [];

  async function analyze() {
    suggestBtn.setAttribute("disabled", "true");
    suggestFb.textContent = "Analyzing usage…";
    proposalsHost.replaceChildren();
    picks.length = 0;
    try {
      const { proposals } = await api.adminSuggestFeedback(7, 3);
      if (!proposals.length) {
        suggestFb.textContent = "No strong candidates in the last 7 days.";
        sendSelBtn.style.display = "none";
        return;
      }
      suggestFb.textContent = `${proposals.length} suggested — review, edit, and send.`;
      for (const p of proposals) {
        const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
        cb.checked = true;
        const promptEl = el("textarea", { class: "control-input", rows: "2" }) as HTMLTextAreaElement;
        promptEl.value = p.prompt;
        picks.push({ email: p.email, app_key: p.app_key, promptEl, cb });
        proposalsHost.append(
          el("div", { class: "proposal" }, [
            el("div", { class: "proposal-head" }, [
              cb,
              el("span", { class: "req-name" }, [p.name]),
              el("span", { class: "muted-sm" }, [p.email]),
              el("span", { class: "sense-tag" }, [p.app_name]),
              el("span", { class: "muted-sm" }, [p.why]),
            ]),
            promptEl,
          ]),
        );
      }
      sendSelBtn.style.display = "";
    } catch {
      suggestFb.textContent = "Couldn't analyze — try again.";
    } finally {
      suggestBtn.removeAttribute("disabled");
    }
  }
  suggestBtn.addEventListener("click", () => void analyze());
  sendSelBtn.addEventListener("click", async () => {
    const items = picks
      .filter((s) => s.cb.checked)
      .map((s) => ({ email: s.email, app_key: s.app_key, prompt: s.promptEl.value.trim() }));
    if (!items.length) {
      suggestFb.textContent = "Nothing selected.";
      return;
    }
    if (!window.confirm(`Send ${items.length} personalized feedback requests?`)) return;
    sendSelBtn.setAttribute("disabled", "true");
    suggestFb.textContent = "Sending…";
    try {
      const res = await api.adminSendSuggested(items);
      suggestFb.textContent = `Sent ${res.created}${res.mail_configured ? `, emailed ${res.emailed}` : " (email off — tracked only)"}.`;
      proposalsHost.replaceChildren();
      sendSelBtn.style.display = "none";
    } catch {
      suggestFb.textContent = "Couldn't send — try again.";
    } finally {
      sendSelBtn.removeAttribute("disabled");
    }
  });
  body.append(
    el("details", { class: "feedback-request", open: "" }, [
      el("summary", {}, ["✨ Suggest recipients (from usage)"]),
      el("p", { class: "muted-sm" }, [
        "The agent scans the last 7 days of launches/views and drafts a personalized, " +
          "app-specific ask for the most active users. Review, edit, and send.",
      ]),
      el("div", { class: "feedback-actions" }, [suggestBtn, suggestFb]),
      proposalsHost,
      sendSelBtn,
    ]),
  );

  // ── request feedback (reuse Team filters to pick recipients) ──
  const grantsByEmail = new Map<string, string[]>();
  for (const g of grants) {
    const a = grantsByEmail.get(g.user_email) ?? [];
    a.push(g.app_key);
    grantsByEmail.set(g.user_email, a);
  }
  const hasApp = (u: AdminUser, key: string) =>
    !key || isBlanket(u) || (grantsByEmail.get(u.email) ?? []).includes(key);

  let fText = "";
  let fApp = "";
  let fRole = "";
  const checked = new Set<string>();
  const matches = (u: AdminUser) => {
    const q = fText.trim().toLowerCase();
    if (q && !(u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q)))
      return false;
    if (fRole && roleOf(u) !== fRole) return false;
    return hasApp(u, fApp);
  };

  const search = el("input", { class: "control-input", type: "search", placeholder: "Name or email" }) as HTMLInputElement;
  const appSel = el("select", { class: "control-input control-mini" }, [
    el("option", { value: "" }, ["Any app"]),
    ...apps.map((a) => el("option", { value: a.key }, [a.name])),
  ]) as HTMLSelectElement;
  const roleSel = el("select", { class: "control-input control-mini" }, [
    el("option", { value: "" }, ["Any role"]),
    el("option", { value: "member" }, ["Members"]),
    el("option", { value: "admin" }, ["Admins"]),
    el("option", { value: "super_admin" }, ["Super Admins"]),
  ]) as HTMLSelectElement;
  const campaign = el("input", { class: "control-input", type: "text", placeholder: "e.g. 2026-Q3" }) as HTMLInputElement;
  const selectAll = el("input", { type: "checkbox" }) as HTMLInputElement;
  const listHost = el("div", { class: "recipient-list" });
  const sendBtn = el("button", { class: "btn btn-primary", type: "button" }, ["Send request"]) as HTMLButtonElement;
  const sendFb = el("span", { class: "muted-sm" });

  function targets(): string[] {
    const shown = users.filter(matches);
    return checked.size ? [...checked] : shown.map((u) => u.email);
  }
  function paintList() {
    const shown = users.filter(matches);
    const n = targets().length;
    sendBtn.textContent = `Send request to ${n} ${n === 1 ? "person" : "people"}`;
    listHost.replaceChildren(
      ...(shown.length
        ? shown.map((u) => {
            const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
            cb.checked = checked.has(u.email);
            cb.addEventListener("change", () => {
              if (cb.checked) checked.add(u.email);
              else checked.delete(u.email);
              paintList();
            });
            return el("label", { class: "recipient" }, [
              cb,
              el("span", {}, [u.name || u.email]),
              el("span", { class: "muted-sm" }, [u.email]),
            ]);
          })
        : [el("p", { class: "muted-sm" }, ["No people match."])]),
    );
  }
  search.addEventListener("input", () => {
    fText = search.value;
    paintList();
  });
  appSel.addEventListener("change", () => {
    fApp = appSel.value;
    paintList();
  });
  roleSel.addEventListener("change", () => {
    fRole = roleSel.value;
    paintList();
  });
  selectAll.addEventListener("change", () => {
    const shown = users.filter(matches);
    if (selectAll.checked) shown.forEach((u) => checked.add(u.email));
    else shown.forEach((u) => checked.delete(u.email));
    paintList();
  });
  sendBtn.addEventListener("click", async () => {
    const emails = targets();
    if (!emails.length) {
      sendFb.textContent = "No recipients.";
      return;
    }
    if (!window.confirm(`Send a feedback request to ${emails.length} people?`)) return;
    sendBtn.setAttribute("disabled", "true");
    sendFb.textContent = "Sending…";
    try {
      const res = await api.adminRequestFeedback(emails, campaign.value.trim() || undefined);
      const emailed = res.mail_configured ? `, emailed ${res.emailed}` : " (email off — tracked only)";
      const skipped = res.skipped.length ? `, ${res.skipped.length} unknown skipped` : "";
      sendFb.textContent = `Sent ${res.created}${emailed}${skipped}.`;
      checked.clear();
      selectAll.checked = false;
      paintList();
    } catch {
      sendFb.textContent = "Couldn't send — try again.";
    } finally {
      sendBtn.removeAttribute("disabled");
    }
  });

  body.append(
    el("details", { class: "feedback-request" }, [
      el("summary", {}, ["Request feedback"]),
      el("div", { class: "grant-form team-filters" }, [
        el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Search"]), search]),
        el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Has access to"]), appSel]),
        el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Role"]), roleSel]),
        el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Campaign"]), campaign]),
      ]),
      el("label", { class: "recipient select-all" }, [selectAll, el("span", {}, ["Select all shown"])]),
      listHost,
      el("div", { class: "feedback-actions" }, [sendBtn, sendFb]),
    ]),
  );
  paintList();

  // ── responses (act / shelve) ──
  body.append(el("h3", { class: "insights-sub" }, ["Responses"]));
  const filterSel = el("select", { class: "control-input control-mini" }, [
    el("option", { value: "new" }, ["New"]),
    el("option", { value: "actioned" }, ["Actioned"]),
    el("option", { value: "shelved" }, ["Shelved"]),
    el("option", { value: "" }, ["All"]),
  ]) as HTMLSelectElement;
  const respHost = el("div", { class: "feedback-list" });
  body.append(
    el("div", { class: "feedback-filter" }, [el("span", { class: "eyebrow" }, ["Show"]), filterSel]),
    respHost,
  );

  function field(label: string, val: string | null) {
    return val
      ? el("div", { class: "fb-field" }, [el("span", { class: "fb-label" }, [label]), el("span", {}, [val])])
      : null;
  }
  function responseCard(f: Feedback): HTMLElement {
    const note = el("input", { class: "control-input control-mini", type: "text", placeholder: "Note (optional)" }) as HTMLInputElement;
    const act = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["Act"]);
    const shelve = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Shelve"]);
    const state = el("span", { class: "muted-sm" }, [f.status !== "new" ? f.status : ""]);
    async function decide(fn: () => Promise<Feedback>, label: string) {
      act.setAttribute("disabled", "true");
      shelve.setAttribute("disabled", "true");
      try {
        await fn();
        state.textContent = label;
        card.classList.add("req-decided");
      } catch {
        state.textContent = "Failed — retry.";
        act.removeAttribute("disabled");
        shelve.removeAttribute("disabled");
      }
    }
    act.addEventListener("click", () =>
      void decide(() => api.adminActFeedback(f.id, note.value.trim() || undefined), "Actioned"),
    );
    shelve.addEventListener("click", () =>
      void decide(() => api.adminShelveFeedback(f.id, note.value.trim() || undefined), "Shelved"),
    );
    const card = el("div", { class: "feedback-item" }, [
      el("div", { class: "fb-head" }, [
        el("span", { class: "req-name" }, [f.user_email]),
        f.sentiment ? el("span", { class: "sense-tag" }, [`${f.sentiment}/5`]) : null,
        el("span", { class: "muted-sm" }, [new Date(f.created_at).toLocaleDateString()]),
      ]),
      field("Likes", f.likes),
      field("Improve", f.improve),
      field("Uses", f.apps_used),
      field("Doesn't use", f.apps_not_used),
      f.status === "new"
        ? el("div", { class: "fb-actions" }, [note, act, shelve, state])
        : el("div", { class: "fb-actions" }, [
            state,
            f.decision_note ? el("span", { class: "muted-sm" }, [`— ${f.decision_note}`]) : null,
          ]),
    ]);
    return card;
  }
  async function loadResponses() {
    respHost.replaceChildren(el("p", { class: "muted-sm" }, ["Loading…"]));
    try {
      const rows = (await api.adminFeedbackList(filterSel.value || undefined)).rows;
      respHost.replaceChildren(
        ...(rows.length ? rows.map(responseCard) : [el("p", { class: "muted" }, ["No feedback here yet."])]),
      );
    } catch {
      respHost.replaceChildren(errorBox("Couldn't load responses."));
    }
  }
  filterSel.addEventListener("change", () => void loadResponses());
  respHost.replaceChildren(
    ...(initial.length ? initial.map(responseCard) : [el("p", { class: "muted" }, ["No new feedback."])]),
  );
}
